import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useDeliveryAuth } from '../context/DeliveryAuthContext';
import './DeliveryDashboard.css';

const DeliveryDashboard = () => {
  const { deliveryBoyData, logout } = useDeliveryAuth();
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [completedToday, setCompletedToday] = useState(0);
  const [earnings, setEarnings] = useState(0);
  const [activeOrder, setActiveOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!deliveryBoyData) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Listen to assigned orders
    const ordersQuery = query(
      collection(db, 'orders'),
      where('deliveryBoy.id', '==', deliveryBoyData.id),
      where('createdAt', '>=', today),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const orders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAssignedOrders(orders);

      // Find active order (not delivered)
      const active = orders.find(
        order => order.status !== 'delivered' && order.status !== 'cancelled'
      );
      setActiveOrder(active || null);

      // Calculate today's completed and earnings
      const completed = orders.filter(order => order.status === 'delivered');
      setCompletedToday(completed.length);

      const totalEarnings = completed.reduce((sum, order) => {
        // Assuming ₹30 per delivery or 10% of order value
        const deliveryFee = order.deliveryCharge || 30;
        return sum + deliveryFee;
      }, 0);
      setEarnings(totalEarnings);

      setLoading(false);
    });

    return () => unsubscribe();
  }, [deliveryBoyData]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
      navigate('/delivery/login');
    }
  };

  const handleViewActiveOrder = () => {
    if (activeOrder) {
      navigate('/delivery/active');
    }
  };

  if (loading) {
    return (
      <div className="delivery-dashboard">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="delivery-dashboard">
      {/* Header */}
      <div className="delivery-header">
        <div className="delivery-header-content">
          <div>
            <h1>🛵 Delivery Dashboard</h1>
            <p>Welcome, {deliveryBoyData?.name || 'Delivery Boy'}!</p>
          </div>
          <div className="header-actions">
            <button className="nav-btn" onClick={() => navigate('/delivery/history')}>
              📋
            </button>
            <button className="nav-btn" onClick={() => navigate('/delivery/profile')}>
              👤
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{assignedOrders.length}</h3>
            <p>Today's Orders</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{completedToday}</h3>
            <p>Completed</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>₹{earnings}</h3>
            <p>Earnings</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{activeOrder ? '🚴' : '✨'}</div>
          <div className="stat-content">
            <h3>{activeOrder ? 'Active' : 'Free'}</h3>
            <p>Status</p>
          </div>
        </div>
      </div>

      {/* Active Delivery */}
      {activeOrder ? (
        <div className="active-delivery-section">
          <h2>🎯 Active Delivery</h2>
          <div className="active-order-card" onClick={handleViewActiveOrder}>
            <div className="order-header">
              <div>
                <h3>Order #{activeOrder.orderNumber}</h3>
                <p className="order-time">
                  {activeOrder.createdAt?.toDate?.()?.toLocaleTimeString() || 'Just now'}
                </p>
              </div>
              <div className={`status-badge ${activeOrder.status}`}>
                {activeOrder.status === 'confirmed' && 'Ready for Pickup'}
                {activeOrder.status === 'prepared' && 'Ready for Pickup'}
                {activeOrder.status === 'outForDelivery' && 'Out for Delivery'}
              </div>
            </div>

            <div className="order-customer">
              <p><strong>Customer:</strong> {activeOrder.customerName}</p>
              <p><strong>Phone:</strong> {activeOrder.customerPhone}</p>
              <p><strong>Address:</strong> {activeOrder.address?.building}, {activeOrder.address?.street}</p>
            </div>

            <div className="order-items">
              <p><strong>Items:</strong> {activeOrder.items?.length || 0} item(s)</p>
              <p><strong>Total:</strong> ₹{activeOrder.totalAmount}</p>
              <p><strong>Payment:</strong> {activeOrder.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Paid Online'}</p>
            </div>

            <button className="view-details-btn">
              View Full Details →
            </button>
          </div>
        </div>
      ) : (
        <div className="no-active-delivery">
          <div className="no-active-icon">🎉</div>
          <h3>No Active Delivery</h3>
          <p>You'll be notified when a new order is assigned to you</p>
        </div>
      )}

      {/* Recent Deliveries */}
      <div className="recent-deliveries-section">
        <h2>📋 Recent Deliveries</h2>
        {assignedOrders.length > 0 ? (
          <div className="orders-list">
            {assignedOrders.slice(0, 5).map(order => (
              <div
                key={order.id}
                className={`order-item ${order.status === 'delivered' ? 'completed' : ''}`}
              >
                <div className="order-item-header">
                  <div>
                    <h4>Order #{order.orderNumber}</h4>
                    <p className="order-time">
                      {order.createdAt?.toDate?.()?.toLocaleString() || 'Recently'}
                    </p>
                  </div>
                  <div className={`status-badge ${order.status}`}>
                    {order.status === 'delivered' && '✅ Delivered'}
                    {order.status === 'cancelled' && '❌ Cancelled'}
                    {order.status === 'outForDelivery' && '🚴 Delivering'}
                    {order.status === 'prepared' && '📦 Ready'}
                    {order.status === 'confirmed' && '⏳ Confirmed'}
                  </div>
                </div>
                <div className="order-item-details">
                  <p>{order.customerName} • ₹{order.totalAmount}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-orders">
            <p>No deliveries assigned yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryDashboard;
